import { useState } from "react";
import api, { apiError, API } from "@/lib/api";
import { fmtDate } from "@/lib/utils";
import { toast } from "sonner";
import { BookingChat } from "@/components/BookingChat";
import { useAuth } from "@/context/AuthContext";
import { Mail, Phone, ChevronDown, ChevronUp, CheckCircle2, XCircle, Flag, Trash2 } from "lucide-react";

const STATUS_CLS = { reserviert: "bg-amber-500/15 text-amber-400", bestaetigt: "bg-emerald-500/15 text-emerald-400", abgeschlossen: "bg-sky-500/15 text-sky-300", storniert: "bg-red-500/15 text-red-300" };
const NEXT = { reserviert: ["bestaetigt", "storniert"], bestaetigt: ["abgeschlossen", "storniert"] };
const LABEL = { bestaetigt: "Final bestätigen", abgeschlossen: "Als abgeschlossen markieren", storniert: "Stornieren" };
const ICON = { bestaetigt: CheckCircle2, abgeschlossen: Flag, storniert: XCircle };

export function AdminBookings({ useData }) {
  const { data, loading, reload } = useData("/admin/bookings");
  const { user } = useAuth();
  const [openId, setOpenId] = useState(null);

  const setStatus = async (b, status) => {
    if (!window.confirm(`Buchung ${b.route_label} (${b.contact_name || "Kunde"}): ${LABEL[status]}?${status === "storniert" && b.payment_status === "paid" ? "\nDie Zahlung wird als erstattet markiert." : ""}`)) return;
    try { await api.post(`/admin/bookings/${b.id}/status`, null, { params: { status } }); toast.success(`Status: ${status}`); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (b) => {
    if (window.prompt(`Buchung ${b.route_label} von ${b.contact_name || "Kunde"} (${b.contact_email || "ohne E-Mail"}) endgültig löschen?\nZur Bestätigung LOESCHEN eingeben:`) !== "LOESCHEN") return;
    try { await api.delete(`/admin/bookings/${b.id}`, { params: { confirm: "LOESCHEN" } }); toast.success("Buchung gelöscht (Sicherung im Archiv)"); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (loading) return <div className="text-slate-400">Lädt…</div>;
  return (
    <div className="space-y-3" data-testid="admin-bookings">
      {data.length === 0 && <div className="eb-card p-8 text-center text-slate-500">Keine Buchungen</div>}
      {data.map((b) => {
        const open = openId === b.id;
        return (
          <div key={b.id} className="eb-card p-4" data-testid={`admin-booking-row-${b.id}`}>
            <button onClick={() => setOpenId(open ? null : b.id)} className="w-full flex flex-wrap items-center justify-between gap-3 text-left" data-testid={`admin-booking-toggle-${b.id}`}>
              <div>
                <div className="font-head font-semibold flex items-center gap-2 flex-wrap">{b.route_label}
                  <span className={`eb-badge ${STATUS_CLS[b.status] || "bg-slate-800 text-slate-300"}`} data-testid={`admin-booking-status-${b.id}`}>{b.status}</span>
                  <span className="eb-badge bg-slate-800 text-slate-300">Zahlung: {b.payment_status}</span>
                </div>
                <div className="text-sm text-slate-400 mt-1">{fmtDate(b.created_at)} · Abfahrt {fmtDate(b.abfahrt)} · {b.partner_name} · <span className="mono text-[#FF5A1F]">{b.customer_price} €</span> · <span className="text-slate-300">{b.contact_name || "Unbekannt"}</span>{b.contact_email ? ` · ${b.contact_email}` : ""}</div>
              </div>
              {open ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
            </button>
            {open && (
              <div className="border-t border-slate-800 mt-3 pt-4 grid lg:grid-cols-2 gap-5 text-sm" data-testid={`admin-booking-detail-${b.id}`}>
                <div className="space-y-3">
                  <div>
                    <div className="eb-label mb-1.5">Kunde</div>
                    <div className="font-semibold">{b.contact_name || <span className="text-slate-500 font-normal">Kein Name</span>}</div>
                    {b.contact_email && <a href={`mailto:${b.contact_email}`} className="flex items-center gap-1.5 text-[#FF5A1F] hover:underline mt-1" data-testid={`admin-booking-mailto-${b.id}`}><Mail className="w-3.5 h-3.5" /> {b.contact_email}</a>}
                    {b.contact_phone && <a href={`tel:${b.contact_phone.replace(/\s/g, "")}`} className="flex items-center gap-1.5 text-[#FF5A1F] hover:underline mt-1"><Phone className="w-3.5 h-3.5" /> {b.contact_phone}</a>}
                  </div>
                  <div>
                    <div className="eb-label mb-1.5">Buchung</div>
                    <div>Nr. <span className="mono">{b.id.slice(0, 8).toUpperCase()}</span> · Partner {b.partner_name} · Match {b.match_score} %</div>
                    <div className="text-slate-400 mt-1">Kundenpreis {b.customer_price} € · Partnerpreis {b.partner_price} € · Provision {b.commission} €</div>
                    <div className="text-slate-400 mt-1">Zahlung: {b.payment_status}{b.payment_provider ? ` (${b.payment_provider})` : ""}{b.invoice_number ? <> · Rechnung <a href={`${API}/bookings/${b.id}/invoice`} target="_blank" rel="noreferrer" className="text-[#FF5A1F] underline">{b.invoice_number}</a></> : ""}</div>
                    {b.transport_status && <div className="text-slate-400 mt-1">Transport: {b.transport_status}</div>}
                  </div>
                  <div className="flex flex-wrap gap-2 pt-2">
                    {(NEXT[b.status] || []).map((s) => { const I = ICON[s]; return (
                      <button key={s} onClick={() => setStatus(b, s)} className={s === "storniert" ? "eb-btn-outline text-red-300" : "eb-btn-primary"} data-testid={`admin-booking-${s}-${b.id}`}><I className="w-4 h-4" /> {LABEL[s]}</button>
                    ); })}
                    <button onClick={() => del(b)} className="eb-btn-ghost text-red-400" data-testid={`admin-booking-delete-${b.id}`}><Trash2 className="w-4 h-4" /> Buchung löschen</button>
                  </div>
                </div>
                <div>
                  <div className="eb-label mb-1.5">Nachrichten zur Buchung</div>
                  <BookingChat bookingId={b.id} meId={user?.id} />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
